import React, { useEffect, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { FieldValues, useFormContext, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "../../app/hook";
import { useLazyUrlCheckerQuery } from "../../app/services/chatbot-rtk.services";
import {
  handleSetPlugAndPlayInfos,
  handleSetStepExtraData,
  wizardState
} from "./botSlice";
import { ConnectForm } from "./BotWizard";
import InfoMessageAlert from "./InfoMessageAlert";
import { AIKittyType } from "./types";

const PlugAndPlayDomain = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    watch,
    clearErrors,
    setError,
    setValue,
    getValues,
    formState: { errors }
  } = useFormContext();
  const wizard = useAppSelector(wizardState);

  const [checkUrl, checkUrlResults] = useLazyUrlCheckerQuery();

  const [isValidDomain, setIsValidDomain] = useState(false);

  const handleTestSiteMapExistence = () => {
    setIsValidDomain(false);

    if (
      !watch("address") ||
      watch("address") === "" ||
      !/^(www\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\.[a-zA-Z()]{2,6}\b([-a-zA-Z0-9()!@:%_\\+.~#?&\\/\\/=]*)/.test(
        watch("address")
      )
    )
      return setError("address", {
        type: "custom",
        message: t("Wizard/PlugAndPlayInfos/key15")
      });

    checkUrl(`https://${getValues("address")}`)
      .unwrap()
      .then((res) => {
        setIsValidDomain(true);
        setValue("address", res.address.replace("https://", ""));
        setValue("isVerified", true);

        clearErrors(["address", "isVerified"]);

        handleUpdate(res.address);

        dispatch(
          handleSetStepExtraData({
            links: res.links,
            oldDomain: res.address,
            isNewRequest: true
          })
        );
      })
      .catch((err) => {
        setIsValidDomain(false);
        setValue("isVerified", false);

        setError("address", {
          type: "custom",
          message: t("Wizard/PlugAndPlayInfos/key13")
        });
      });
  };

  const handleUpdate = (address: string) => {
    dispatch(
      handleSetPlugAndPlayInfos({
        address
      })
    );
  };

  useEffect(() => {
    clearErrors();

    if ((wizard.bot_config as AIKittyType).plug_and_play) {
      setValue(
        "address",
        (wizard.bot_config as AIKittyType).plug_and_play?.address.replace(
          "https://",
          ""
        )
      );
      setValue("isVerified", true);
    }
  }, [(wizard.bot_config as AIKittyType).plug_and_play]);

  return (
    <ConnectForm>
      {({
        register
      }: // formState: { errors }
      UseFormReturn<FieldValues, any>) => (
        <div className="plug-and-play tw-py-4 md:tw-px-4">
          <div className="tw-w-full tw-min-h-[166px] tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-inline-flex">
            <div className="tw-self-stretch tw-min-h-[60px] tw-flex-col tw-justify-start tw-items-start tw-gap-8 tw-flex">
              <div className="tw-self-stretch tw-min-h-[60px] tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
                <div className="tw-self-stretch tw-text-gray-700 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
                  1. {t("Wizard/PlugAndPlayInfos/key1")}
                </div>
                <div className="tw-self-stretch tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                  {t("Wizard/PlugAndPlayInfos/key2")}
                </div>
              </div>
            </div>

            <InfoMessageAlert texts={[t("Wizard/PlugAndPlayAlert/key1")]} />

            <div className="tw-self-stretch tw-min-h-[82px] tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-flex">
              <div className="tw-self-stretch tw-min-h-[82px] tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-flex">
                <label
                  htmlFor="address"
                  className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-block"
                >
                  {t("Wizard/PlugAndPlayInfos/key11")}
                </label>

                <div className="md:tw-w-2/3 tw-w-full tw-self-stretch tw-min-h-[38px] tw-rounded-md tw-shadow tw-border tw-border-gray-200 tw-justify-start tw-items-center tw-inline-flex">
                  <div className="tw-pl-[13px] tw-pr-3 tw-py-[9px] tw-bg-gray-50 tw-rounded-tl-md tw-rounded-bl-md tw-border tw-border-gray-100 tw-justify-start tw-items-center tw-flex">
                    <div className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                      https://
                    </div>
                  </div>

                  <input
                    className="tw-grow tw-shrink tw-basis-0 tw-min-h-[38px] tw-pl-3 tw-pr-[13px] tw-py-[9px] tw-bg-white disabled:tw-bg-gray-100 disabled:tw-cursor-not-allowed tw-rounded-tr-md tw-rounded-br-md tw-justify-start tw-items-center tw-flex placeholder:tw-text-gray-400 tw-text-gray-900 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-border-0 tw-w-full focus:tw-ring-0 focus:tw-outline-none"
                    placeholder="example.com/sitemap.xml"
                    id="address"
                    type="url"
                    {...register("address", {
                      onChange() {
                        setIsValidDomain(false);
                      }
                    })}
                  />

                  <Button
                    variant="light"
                    onClick={handleTestSiteMapExistence}
                    disabled={!!errors.address || checkUrlResults.isFetching}
                    className="disabled:tw-bg-gray-100 disabled:tw-cursor-not-allowed"
                  >
                    {checkUrlResults.isFetching ? (
                      <Spinner size="sm" />
                    ) : (
                      t("Wizard/PlugAndPlayInfos/key16")
                    )}
                  </Button>
                </div>

                {checkUrlResults.isFetching && (
                  <p className="tw-text-sm tw-text-sky-600">
                    {t("Wizard/PlugAndPlayInfos/key17")}
                  </p>
                )}

                {/* {width < 768 && (
                  <div className="tw-resize-y tw-min-h-[72px] tw-rounded-md tw-shadow tw-border tw-border-gray-300 tw-justify-start tw-items-start md:tw-hidden tw-inline-flex">
                    <div className="tw-pl-[13px] tw-pr-3 tw-pt-[9px] tw-pb-[43px] tw-bg-gray-50 tw-rounded-tl-md tw-rounded-bl-md tw-border tw-border-gray-300 tw-justify-start tw-items-center tw-flex">
                      <div className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                        https://
                      </div>
                    </div>
                    <div className="tw-grow tw-shrink tw-basis-0 tw-min-h-[72px] tw-pl-3 tw-pr-[13px] tw-pt-[9px] tw-bg-white tw-rounded-tr-md tw-rounded-br-md tw-justify-start tw-items-start tw-flex">
                      <textarea
                        className="placeholder:tw-text-gray-400 tw-h-full tw-text-gray-900 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-border-0 tw-w-full focus:tw-ring-0 focus:tw-outline-none"
                        placeholder="example.com/sitemap.xml"
                        id="address"
                        rows={3}
                        {...register("address", {
                          onChange() {
                            setIsValidDomain(false);
                          }
                        })}
                      />
                    </div>
                    <Button
                      variant="light"
                      onClick={handleTestSiteMapExistence}
                      disabled={!!errors.address || checkUrlResults.isLoading}
                    >
                      {checkUrlResults.isLoading ? (
                        <Spinner size="sm" />
                      ) : (
                        t("Wizard/PlugAndPlayInfos/key16")
                      )}
                    </Button>
                  </div>
                )} */}

                <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-1 tw-inline-flex">
                  <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none"></div>
                  <div className="tw-grow tw-shrink tw-basis-0 tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                    {t("Wizard/PlugAndPlayInfos/key12")}
                  </div>
                </div>

                {isValidDomain && (
                  <p className="tw-text-sm tw-text-green-600">
                    {t("Wizard/PlugAndPlayInfos/key18")}
                  </p>
                )}

                {!!errors.address && (
                  <span className="tw-text-sm tw-text-red-500 tw-block">
                    {errors?.address?.message as string}
                  </span>
                )}
                {!errors.address && !!errors.isVerified && (
                  <span className="tw-text-sm tw-text-red-500 tw-block">
                    {errors?.isVerified?.message as string}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </ConnectForm>
  );
};

export default PlugAndPlayDomain;
